import { AccountResource, Loadable, OrderManagementResource } from '@eih-netbroker/common';
import { useCallback, useMemo } from 'react';
import { useResource, useResourceHeaders } from '../../../../shared/services/useResource';
import { useOrderCurrencyExchangeState } from '../../components/OrderCurrencyExchangeStateContext';
import { ValidatedOrder } from './useNormalOrderView';

export const useOrderCurrencyExchange = () => {
  const accountResource = useResource(AccountResource);
  const orderResource = useResource(OrderManagementResource);
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const {
    currencyExchangeBalance,
    setCurrencyExchangeBalance,
    exchangeSettlementDate,
    setExchangeSettlementDate,
    exchangeRate,
    setExchangeRate,
  } = useOrderCurrencyExchangeState();

  const loadBalanceForOrder = useCallback(
    async (sourceCurrency: string, mic: string) => {
      return (
        await accountResource.getCurrencyBalanceForOrder(
          {
            sourceCcyCode: sourceCurrency,
            mic,
          },
          getResourceHeaderWithToken(),
        )
      ).data;
    },
    [accountResource, getResourceHeaderWithToken],
  );

  const loadExchangeRateForOrder = useCallback(
    async (sourceCurrency: string, targetCurrency: string, amount: number) => {
      return (
        await orderResource.getExchangeRateForOrder(
          {
            sourceCcyCode: sourceCurrency,
            targetCcyCode: targetCurrency,
            targetAmount: amount,
          },
          getResourceHeaderWithToken(),
        )
      ).data;
    },
    [orderResource, getResourceHeaderWithToken],
  );

  const loadExchangeSettlementDate = useCallback(
    async (mic: string) => {
      return (
        await orderResource.getExchangeSettlementDate(
          {
            mic,
          },
          getResourceHeaderWithToken(),
        )
      ).data;
    },
    [orderResource, getResourceHeaderWithToken],
  );

  /**
   * Gets and updates the currency balance for an order
   */
  const getBalanceForOrder = useCallback(
    async (sourceCurrency: string, order: ValidatedOrder) => {
      try {
        setCurrencyExchangeBalance(Loadable.loading);
        const balance = await loadBalanceForOrder(sourceCurrency, order.instrument.mic);
        const res = Loadable.loaded(balance);
        setCurrencyExchangeBalance(res);
        return res;
      } catch (err) {
        const res = Loadable.failed;
        setCurrencyExchangeBalance(res);
        return res;
      }
    },
    [setCurrencyExchangeBalance, loadBalanceForOrder],
  );

  const getExchangeSettlementDate = useCallback(
    async (order: ValidatedOrder) => {
      try {
        setExchangeSettlementDate(Loadable.loading);
        const settlementDate = await loadExchangeSettlementDate(order.instrument.mic);
        const res = Loadable.loaded(settlementDate);
        setExchangeSettlementDate(res);
        return res;
      } catch (err) {
        const res = Loadable.failed;
        setExchangeSettlementDate(res);
        return res;
      }
    },
    [setExchangeSettlementDate, loadExchangeSettlementDate],
  );

  const getExchangeRateForOrder = useCallback(
    async (sourceCurrency: string, targetCurrency: string, amount: number) => {
      try {
        setExchangeRate(Loadable.loading);
        const rate = await loadExchangeRateForOrder(sourceCurrency, targetCurrency, amount);
        // TODO: remove this after NN-5082 fixed
        // if currencyPair is not set, it means the response is not valid
        if (!rate.currencyPair) {
          const res = Loadable.failed;
          setExchangeRate(res);
          return res;
        }
        const res = Loadable.loaded(rate);
        setExchangeRate(res);
        return res;
      } catch (err) {
        const res = Loadable.failed;
        setExchangeRate(res);
        return res;
      }
    },
    [setExchangeRate, loadExchangeRateForOrder],
  );

  return useMemo(
    () => ({
      getBalanceForOrder,
      getExchangeSettlementDate,
      currencyExchangeBalance,
      setCurrencyExchangeBalance,
      exchangeSettlementDate,
      setExchangeSettlementDate,
      exchangeRate,
      setExchangeRate,
      getExchangeRateForOrder,
      loadExchangeRateForOrder,
    }),
    [
      getBalanceForOrder,
      getExchangeSettlementDate,
      currencyExchangeBalance,
      setCurrencyExchangeBalance,
      exchangeSettlementDate,
      setExchangeSettlementDate,
      exchangeRate,
      setExchangeRate,
      getExchangeRateForOrder,
      loadExchangeRateForOrder,
    ],
  );
};
